import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useIdle, useFirstInteraction, interactNow } from "../lib/defer";
import { isFirstVisit } from "../lib/radio";
import { Wordmark } from "./Shared";
import { WhatsAppButton } from "./WhatsAppButton";
import { WhatsAppGate } from "./WhatsAppGate";

const AskWidget = lazy(() => import("./AskWidget"));
const RadioBubble = lazy(() => import("./RadioBubble").then((m) => ({ default: m.RadioBubble })));
const CookieConsent = lazy(() => import("./CookieConsent").then((m) => ({ default: m.CookieConsent })));

// Same classes and markup as the real launcher in AskWidget.jsx: the visitor sees one unchanged button, the assistant code only
// downloads when they hover, focus or tap it (or interact with the page at all).
export function AskLauncher({ onWake }) {
  const { pathname } = useLocation();
  const isHub = pathname === "/" || pathname === "/business";
  const [visible, setVisible] = useState(!isHub);
  useEffect(() => {
    const onScroll = () => setVisible(!isHub || window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname, isHub]);
  if (!visible) return null;
  const wake = (open) => { if (open) sessionStorage.setItem("vl-ask-open", "1"); onWake(); };
  return (
    <div className={`ask-launcher fixed right-4 z-30 md:right-6 ${/^\/destinations\/[^/]+/.test(pathname) ? "bottom-28 md:bottom-24" : "bottom-20 md:bottom-6"}`} data-testid="ask-widget" data-deferred="true">
      <button type="button" onPointerEnter={() => wake(false)} onFocus={() => wake(false)} onClick={() => wake(true)} data-testid="ask-widget-toggle" aria-expanded={false} aria-label="Ask vistolane"
        className="ml-auto flex h-12 w-12 items-center justify-center gap-3 rounded-full border border-separator bg-background p-0 text-base font-semibold text-foreground shadow-pop transition-[transform,box-shadow] hover:scale-[1.03] hover:shadow-pop md:h-14 md:w-auto md:justify-start md:pl-6 md:pr-2">
        <span className="hidden md:inline-flex items-baseline gap-1.5 whitespace-nowrap">Ask <Wordmark size="text-xl" testId="ask-widget-v" /></span>
        <span className="grid h-10 w-10 place-items-center rounded-full bg-primary text-white"><MessageCircle className="h-4 w-4" /></span>
      </button>
    </div>
  );
}

// Chat assistant and radio load on the first interaction (or when the launcher is touched); a first-time visitor gets the expanded radio player once the page is idle; the cookie banner still appears on its own once the page is idle.
export function DeferredWidgets({ cookieOnly = false }) {
  const idle = useIdle();
  const interacted = useFirstInteraction();
  const [askOpen, setAskOpen] = useState(() => sessionStorage.getItem("vl-ask-open") === "1");
  const [firstVisit] = useState(isFirstVisit);
  const wakeAsk = () => { setAskOpen(true); interactNow(); };
  const ask = askOpen || interacted;
  return (
    <>
      {!cookieOnly && <WhatsAppButton />}
      {!cookieOnly && <WhatsAppGate />}
      {!cookieOnly && !ask && <AskLauncher onWake={wakeAsk} />}
      {!cookieOnly && ask && <Suspense fallback={<AskLauncher onWake={() => {}} />}><AskWidget instant /></Suspense>}
      {!cookieOnly && (interacted || (idle && firstVisit)) && <Suspense fallback={null}><RadioBubble /></Suspense>}
      <Suspense fallback={null}>{idle && <CookieConsent />}</Suspense>
    </>
  );
}
